import React, { useState, useEffect } from 'react';
import {
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Typography,
  Box,
  SelectChangeEvent,
  CircularProgress,
} from '@mui/material';
import { Phone as PhoneIcon } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

interface OutgoingOption {
  id: string;
  number: string;
  name: string;
  is_default?: boolean;
}

const CallingFromSelector: React.FC = () => {
  const { member } = useAuth();
  const [loading, setLoading] = useState(false);
  const [outgoingOptions, setOutgoingOptions] = useState<OutgoingOption[]>([]);
  const [selectedOption, setSelectedOption] = useState<string>('');
  const [hasExtension, setHasExtension] = useState(false);
  const [lastKnownDialGroup, setLastKnownDialGroup] = useState<string | null>(null);

  const fetchOutgoingOptions = React.useCallback(async () => {
    // Don't make API calls if not authenticated or no extension
    if (!member?.extension || !member?.id) return;

    try {
      setLoading(true);
      // Fetch extension details with userOnly flag to get outgoing options
      const response = await api.get(`/endpoints/extensions/${member.extension}`, {
        params: { userOnly: 'true' }
      });

      if (response.data.success && response.data.data) {
        const extensionData = response.data.data.extension || response.data.data;
        const options = extensionData.outgoing_options || [];

        // If we have outgoing options, use them
        if (options.length > 0) {
          setOutgoingOptions(options);

          // Set current selected option
          const currentId = extensionData.outgoing_id || '';
          setSelectedOption(currentId);
          setLastKnownDialGroup(currentId);

          // If no selection but options exist, select the default or first one
          if (!currentId) {
            const defaultOption = options.find((opt: OutgoingOption) => opt.is_default);
            setSelectedOption(defaultOption?.id || options[0].id);
          }

          // Store current selection in localStorage
          if (currentId) {
            localStorage.setItem(`calling_from_${member?.extension}`, currentId);
          }
        } else {
          // No outgoing options available - component will hide itself
          setOutgoingOptions([]);
        }
      }
    } catch (error) {
      console.error('Failed to fetch outgoing options:', error);
      // Don't show anything if we can't fetch options
      setOutgoingOptions([]);
    } finally {
      setLoading(false);
    }
  }, [member]);

  useEffect(() => {
    console.log('CallingFromSelector - member:', member);
    console.log('CallingFromSelector - extension:', member?.extension);

    if (member?.extension) {
      setHasExtension(true);
      fetchOutgoingOptions();
    } else {
      setHasExtension(false);
      setOutgoingOptions([]);
      setSelectedOption('');
    }
  }, [member?.extension]);

  // Listen for extension updates from other components
  useEffect(() => {
    const handleExtensionUpdate = (event: CustomEvent) => {
      console.log('CallingFromSelector - Extension update event received:', event.detail);
      // If the updated extension matches our extension, refresh
      if (event.detail?.extension === member?.extension) {
        fetchOutgoingOptions();
      }
    };

    window.addEventListener('extensionUpdated' as any, handleExtensionUpdate);

    return () => {
      window.removeEventListener('extensionUpdated' as any, handleExtensionUpdate);
    };
  }, [member?.extension]);

  // Refresh when window regains focus or becomes visible
  useEffect(() => {
    const handleFocus = () => {
      console.log('CallingFromSelector - Window focused, checking for updates');
      if (member?.extension) {
        fetchOutgoingOptions();
      }
    };

    const handleVisibilityChange = () => {
      if (!document.hidden && member?.extension) {
        console.log('CallingFromSelector - Page became visible, checking for updates');
        fetchOutgoingOptions();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [member?.extension, fetchOutgoingOptions]);

  // Poll for changes to DialGroup (outgoing_id) every 3 seconds
  useEffect(() => {
    // Don't poll if not authenticated or no extension
    if (!member?.extension || !member?.id) return;

    const checkForChanges = async () => {
      try {
        const response = await api.get(`/endpoints/extensions/${member.extension}`, {
          params: { userOnly: 'true' }
        });

        if (response.data.success && response.data.data) {
          const extensionData = response.data.data.extension || response.data.data;
          const currentOutgoingId = extensionData.outgoing_id || '';

          // Check if the outgoing_id has changed from what we last knew
          if (lastKnownDialGroup !== null && currentOutgoingId !== lastKnownDialGroup) {
            console.log('CallingFromSelector - DialGroup change detected:', {
              old: lastKnownDialGroup,
              new: currentOutgoingId
            });
            // Force a full refresh of options
            fetchOutgoingOptions();
          }

          // Update our last known value
          setLastKnownDialGroup(currentOutgoingId);
        }
      } catch (error) {
        console.error('Failed to check for DialGroup changes:', error);
      }
    };

    // Check every 3 seconds for more responsive updates
    const interval = setInterval(checkForChanges, 3000);

    // Also check immediately on mount
    checkForChanges();

    return () => clearInterval(interval);
  }, [member?.extension, lastKnownDialGroup, fetchOutgoingOptions]);

  const handleChange = async (event: SelectChangeEvent<string>) => {
    const newValue = event.target.value;
    setSelectedOption(newValue);

    // Store in localStorage for persistence
    localStorage.setItem(`calling_from_${member?.extension}`, newValue);

    // Update the extension's outgoing_id with userOnly flag
    try {
      await api.put(`/endpoints/extensions/${member?.extension}`, {
        outgoing_id: newValue
      }, {
        params: { userOnly: 'true' }
      });
    } catch (error) {
      console.error('Failed to update calling from number:', error);
    }
  };

  // Don't show if user has no extension
  if (!hasExtension) {
    console.log('CallingFromSelector - Not showing: no extension');
    return null;
  }

  // Don't show if no options available
  if (!loading && outgoingOptions.length === 0) {
    console.log('CallingFromSelector - Not showing: no options and not loading');
    return null;
  }

  console.log('CallingFromSelector - Rendering with options:', outgoingOptions);

  return (
    <Box sx={{ width: '100%' }}>
      <FormControl
        fullWidth
        size="small"
        variant="outlined"
        disabled={loading}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            backgroundColor: 'background.paper',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          },
          '& .MuiInputLabel-root': {
            fontSize: '0.875rem',
          },
          '& .MuiSelect-select': {
            py: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          },
        }}
      >
        <InputLabel id="calling-from-label">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <PhoneIcon sx={{ fontSize: 16 }} />
            Calling From
          </Box>
        </InputLabel>
        <Select
          labelId="calling-from-label"
          id="calling-from-select"
          value={selectedOption}
          onChange={handleChange}
          label="Calling From"
        >
          {loading ? (
            <MenuItem disabled>
              <CircularProgress size={16} />
              <Typography variant="body2" sx={{ ml: 1 }}>
                Loading...
              </Typography>
            </MenuItem>
          ) : (
            outgoingOptions.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {option.number}
                    </Typography>
                    {option.name && (
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {option.name}
                      </Typography>
                    )}
                  </Box>
                  {option.is_default && (
                    <Typography variant="caption" sx={{ color: 'primary.main' }}>
                      Default
                    </Typography>
                  )}
                </Box>
              </MenuItem>
            ))
          )}
        </Select>
      </FormControl>
    </Box>
  );
};

export default CallingFromSelector;